/// <amd-module name="Build/Diag/Timeline" />
import * as React from "react";

import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { ITfsPageService } from "Tfs/Platform/Page";

import { Dialog } from "VSSUI/Dialog";
import { SimpleTableCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";

import { Build, TimelineRecord } from "Build/Client/Contracts/Build";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

interface ITimelineProps extends IVssComponentProperties {
    onDismiss: () => void;
}

interface ITimelineState {
    content: string;
    sContent: string;
    itemProvider?: ITreeItemProvider<ITimelineItem>;
    sItemProvider?: ITreeItemProvider<ITimelineItem>;
    build?: Build;
    planId?: string;
}

interface ITimelineItem extends TimelineRecord {
    computedName: string;
}

interface ITimelineTreeItem extends ITreeItem<ITimelineItem> {

}

const renderMainCell = (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ITimelineItem>, treeItem: ITreeItemEx<ITimelineItem>): JSX.Element => {
    const record = treeItem.underlyingItem.data;
    const content: JSX.Element = <div className="text-ellipsis t-content">
        {record.computedName}
    </div>;
    return ExpandableTreeCell({
        children: content,
        columnIndex,
        treeColumn,
        treeItem
    });
};

const renderPrevAttemptCell = (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ITimelineItem>, treeItem: ITreeItemEx<ITimelineItem>): JSX.Element => {
    const record = treeItem.underlyingItem.data;
    const content: JSX.Element = <div className="text-ellipsis t-content pre">
        <textarea className="json-data" value={JSON.stringify(record.previousAttempts, null, '\t')}></textarea>
    </div>;
    return SimpleTableCell({
        children: content,
        columnIndex
    });
};

const renderDataCell = (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ITimelineItem>, treeItem: ITreeItemEx<ITimelineItem>): JSX.Element => {
    const record = treeItem.underlyingItem.data;
    const content: JSX.Element = <div className="text-ellipsis t-content pre">
        <textarea className="json-data" value={JSON.stringify(record, null, '\t')}></textarea>
    </div>;
    return SimpleTableCell({
        children: content,
        columnIndex
    });
};

const Space = () => {
    return <div className="space"></div>;
}
// tslint:disable:member-ordering
export class Timeline extends VssComponent<ITimelineProps, ITimelineState> {
    private _tId: ObservableValue<string> = new ObservableValue<string>("");
    private _cId: ObservableValue<string> = new ObservableValue<string>("0");

    private _onSize = (event: MouseEvent, index: number, width: number) => {
        (this._tableColumns[index].width as ObservableValue<number>).value = width;
    }

    private _tableColumns: ITreeColumn<ITimelineItem>[] = [
        {
            id: "rec",
            name: "Record",
            renderCell: renderMainCell,
            width: new ObservableValue(-50),
            onSize: this._onSize
        },
        {
            id: "prevat",
            name: "Previous Attempts",
            renderCell: renderPrevAttemptCell,
            width: new ObservableValue(-20),
            onSize: this._onSize
        },
        {
            id: "data",
            name: "Data",
            renderCell: renderDataCell,
            width: new ObservableValue(-30),
            onSize: this._onSize
        }
    ];

    constructor(props: ITimelineProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            content: "",
            sContent: ""
        };
    }

    public render() {
        return <Dialog
            className="timeline-view"
            calloutClassName="timeline-panel"
            escDismiss={true}
            showCloseButton={true}
            titleProps={{ text: "" }}
            onDismiss={this.props.onDismiss}>
            <div className="timeline-content">
                <h3>Current context:</h3>
                <div className="flex mute" dangerouslySetInnerHTML={{ __html: this.state.content }}></div>
                <Space />
                <div
                    className="flex-grow">
                    {
                        this.state.itemProvider && (
                            <Tree<ITimelineItem>
                                columns={this._tableColumns}
                                itemProvider={this.state.itemProvider}
                                onToggle={(event, treeItem: ITreeItemEx<ITimelineItem>) => {
                                    (this.state.itemProvider as ITreeItemProvider<ITimelineItem>).toggle(treeItem.underlyingItem);
                                }}
                                scrollable={true}
                            />
                        )
                    }
                </div>
                <div className="search-area flex-column">
                    <h3>Additional Search:</h3>
                    <div className="mute" dangerouslySetInnerHTML={{ __html: this.state.sContent }}></div>
                    <Space />
                    <div className="flex flex-row">
                        <TextField
                            value={this._tId}
                            onChange={(e, newValue) => (this._tId.value = newValue)}
                            placeholder="Enter timeline Id"
                        />
                        <span>&nbsp;&nbsp;</span>
                        <TextField
                            value={this._cId}
                            onChange={(e, newValue) => (this._cId.value = newValue)}
                            placeholder="Enter change Id"
                        />
                        <Button
                            iconProps={
                                {
                                    iconName: "Search"
                                }
                            }
                            onClick={this._search}
                        />
                    </div>
                    <div className="flex-grow">
                        {
                            this.state.sItemProvider && (
                                <Tree<ITimelineItem>
                                    columns={this._tableColumns}
                                    itemProvider={this.state.sItemProvider}
                                    onToggle={(event, treeItem: ITreeItemEx<ITimelineItem>) => {
                                        (this.state.sItemProvider as ITreeItemProvider<ITimelineItem>).toggle(treeItem.underlyingItem);
                                    }}
                                    scrollable={true}
                                />
                            )
                        }
                    </div>
                </div>
                {
                    this.state.planId && (
                        <div className="flex-column">
                            <h3>Sql query:</h3>
                            {
                                <textarea className="code">
                                    {
                                        `
                                            declare @internalPlanId INT;
                                            select @internalPlanId = InternalId
                                            from Task.tbl_Plan 
                                            where PartitionId > 0
                                            and PlanId = '${this.state.planId}'

                                            select ts.FullStringValue as Type, ts1.FullStringValue as Name, ts2.FullStringValue as RefName, tr.[Order], tr.Identifier, tr.RecordId, tr.ParentId, tr.StartTime, tr.FinishTime, tr.State, tr.Result, tr.Attempt, tr.PlanId
                                            from Task.tbl_TimelineRecord tr
                                            join Task.tbl_TimelineString ts
                                            on tr.PartitionId = ts.PartitionId
                                            and tr.DataspaceId = ts.DataspaceId
                                            and tr.TypeId = ts.StringId
                                            join Task.tbl_TimelineString ts1
                                            on tr.PartitionId = ts1.PartitionId
                                            and tr.DataspaceId = ts1.DataspaceId
                                            and tr.NameId = ts1.StringId
                                            join Task.tbl_TimelineString ts2
                                            on tr.PartitionId = ts2.PartitionId
                                            and tr.DataspaceId = ts2.DataspaceId
                                            and tr.RefNameId = ts2.StringId
                                            where tr.PartitionId > 0
                                            and tr.PlanId = @internalPlanId
                                            order by Type, tr.StartTime, tr.[Order] asc
                                        `
                                    }
                                </textarea>
                            }
                        </div>
                    )
                }

                <div className="build-area flex-column">
                    <h3>Current build:</h3>
                    {
                        this.state.build && (
                            <div className="pre">
                                <textarea className="json-data" value={JSON.stringify(this.state.build, null, '\t')} />
                            </div>
                        )
                    }
                </div>
            </div>
        </Dialog>
    }

    public componentDidMount() {
        this._initialize();
    }

    private _search = () => {
        this._initialize(this._tId.value, parseInt(this._cId.value));
    }

    private _initialize(timelineId?: string, changeId?: number) {
        const hs = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const buildId = parseInt(hs.getState()["buildId"]);
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectId = tfsPageData && tfsPageData.project && tfsPageData.project.id;
        if (buildId && projectId) {
            const client = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            if (!this.state.build) {
                client.getBuild(projectId, buildId).then(b => {
                    this.setState({
                        build: b,
                        planId: b.orchestrationPlan.planId
                    });
                });
            }

            client.getBuildTimeline(projectId, buildId, timelineId, changeId).then(t => {
                if (!t) {
                    if (timelineId) {
                        this.setState({
                            sContent: `Timeline ${timelineId} not found`,
                        });
                    }
                    else {
                        this.setState({
                            content: `Timeline for build:${buildId} not found`,
                        });
                    }
                    return;
                }

                console.log("Diag: Timeline: ", t);
                const items: ITimelineTreeItem[] = [];

                const stages: TimelineRecord[] = [];
                const phases: TimelineRecord[] = [];
                const jobs: TimelineRecord[] = [];
                const tasks: TimelineRecord[] = [];

                // Feature flags for checkpoints/environments to work - 
                // exec prc_SetRegistryValue 1, '#\FeatureAvailability\Entries\PipelineChecks.EnableApprovalsUI\AvailabilityState\', '1' 
                // exec prc_SetRegistryValue 1, '#\FeatureAvailability\Entries\DistributedTask.ResourceApprovals\AvailabilityState\', '1' 
                const checkpoints: TimelineRecord[] = [];

                const groups: TimelineRecord[] = [];

                const stageMap: { [key: string]: number; } = {};
                const phaseMap: { [key: string]: number; } = {};
                const jobMap: { [key: string]: number; } = {};
                const taskMap: { [key: string]: number; } = {};

                let stageIndex = 0;
                let phaseIndex = 0;
                let jobIndex = 0;
                let taskIndex = 0;
                // https://dev.azure.com/mseng/AzureDevOps/_git/AzureDevOps?path=%2FDistributedTask%2FSdk%2FServer%2FTimelineRecordTypes.cs&version=GBmaster
                t.records.forEach(r => {
                    if (r.type.toLowerCase() === "stage") {
                        stages.push(r);
                        stageMap[r.id] = stageIndex++;
                    }
                    if (r.type.toLowerCase() === "phase") {
                        phases.push(r);
                        phaseMap[r.id] = phaseIndex++;
                    }
                    if (r.type.toLowerCase() === "job") {
                        jobs.push(r);
                        jobMap[r.id] = jobIndex++;
                    }
                    if (r.type.toLowerCase() === "task") {
                        tasks.push(r);
                        taskMap[r.id] = taskIndex++;
                    }
                    if (r.type.toLowerCase().indexOf("checkpoint") > -1) {
                        checkpoints.push(r);
                    }
                    if (r.type.toLowerCase() === "group") {
                        groups.push(r);
                    }
                });

                const stageTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                stages.forEach(s => {
                    const stageItem = {
                        data: this._getItem(s)
                    };
                    stageTreeMap[s.id] = stageItem;
                    items.push(stageItem);
                });

                this._sort(items);
                console.log("Stages:", stages, { ...items }, stageTreeMap);

                const phaseTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                phases.forEach(p => {
                    const stageTree = stageTreeMap[p.parentId];
                    if (stageTree) {
                        console.log("Linking phase ", p, " to stage ", { ...stageTree });
                        stageTree.childItems = stageTree.childItems || [];
                        const phaseItem = {
                            data: this._getItem(p)
                        };
                        phaseTreeMap[p.id] = phaseItem;
                        stageTree.childItems.push(phaseItem);
                        this._sort(stageTree.childItems);
                    }
                    else {
                        items.push({
                            data: this._getItem(p, true)
                        });
                    }
                });

                console.log("Phases:", phases, { ...items }, phaseTreeMap);

                const jobTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                jobs.forEach(p => {
                    const phaseTree = phaseTreeMap[p.parentId];
                    if (phaseTree) {
                        console.log("Linking job ", p, " to phase ", { ...phaseTree });
                        phaseTree.childItems = phaseTree.childItems || [];
                        const jobItem = {
                            data: this._getItem(p)
                        };
                        jobTreeMap[p.id] = jobItem;
                        phaseTree.childItems.push(jobItem);
                        this._sort(phaseTree.childItems);
                    }
                    else {
                        items.push({
                            data: this._getItem(p, true)
                        });
                    }
                });

                console.log("Jobs:", jobs, items, jobTreeMap);

                const taskTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                tasks.forEach(p => {
                    const jobTree = jobTreeMap[p.parentId];
                    // sometimes server side tasks like cloud build, link a task to the phase directly
                    const phaseTree = phaseTreeMap[p.parentId];
                    if (jobTree) {
                        console.log("Linking task ", p, " to job ", { ...jobTree });
                        jobTree.childItems = jobTree.childItems || [];
                        const taskItem = {
                            data: this._getItem(p)
                        };
                        jobTree.childItems.push(taskItem);
                        taskTreeMap[p.id] = taskItem;
                        this._sort(jobTree.childItems);
                    }

                    else if (phaseTree) {
                        console.log("Linking task ", p, " to phase ", { ...phaseTree });
                        phaseTree.childItems = phaseTree.childItems || [];
                        const taskItem = {
                            data: this._getItem(p)
                        };
                        phaseTree.childItems.push(taskItem);
                        taskTreeMap[p.id] = taskItem;
                        this._sort(phaseTree.childItems);
                    }
                    else {
                        items.push({
                            data: this._getItem(p, true)
                        });
                    }
                });

                console.log("Tasks:", tasks, { ...items }, taskTreeMap);

                // add checkpoints and groups

                // see if checkpoints/groups are child of previous records
                const checkpointTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                checkpoints.forEach(x => {
                    const stageTree = stageTreeMap[x.parentId];
                    if (stageTree) {
                        console.log("Linking checkpoint ", x, " to stage ", { ...stageTree });
                        stageTree.childItems = stageTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        checkpointTreeMap[x.id] = checkpointItem;
                        stageTree.childItems.push(checkpointItem);
                        this._sort(stageTree.childItems);
                    }

                    const phaseTree = phaseTreeMap[x.parentId];
                    if (phaseTree) {
                        console.log("Linking checkpoint ", x, " to phase ", { ...phaseTree });
                        phaseTree.childItems = phaseTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        checkpointTreeMap[x.id] = checkpointItem;
                        phaseTree.childItems.push(checkpointItem);
                        this._sort(phaseTree.childItems);
                    }

                    const jobTree = jobTreeMap[x.parentId];
                    if (jobTree) {
                        console.log("Linking checkpoint ", x, " to job ", { ...jobTree });
                        jobTree.childItems = jobTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        checkpointTreeMap[x.id] = checkpointItem;
                        jobTree.childItems.push(checkpointItem);
                        this._sort(jobTree.childItems);
                    }

                    const taskTree = taskTreeMap[x.parentId];
                    if (taskTree) {
                        console.log("Linking checkpoint ", x, " to task ", { ...taskTree });
                        taskTree.childItems = taskTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        checkpointTreeMap[x.id] = checkpointItem;
                        taskTree.childItems.push(checkpointItem);
                        this._sort(taskTree.childItems);
                    }
                });

                console.log("Checkpoints:", checkpoints, { ...items }, checkpointTreeMap);

                const groupTreeMap: { [key: string]: ITimelineTreeItem; } = {};
                groups.forEach(x => {
                    const stageTree = stageTreeMap[x.parentId];
                    if (stageTree) {
                        console.log("Linking group ", x, " to stage ", { ...stageTree });
                        stageTree.childItems = stageTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        groupTreeMap[x.id] = checkpointItem;
                        stageTree.childItems.push(checkpointItem);
                        this._sort(stageTree.childItems);
                    }

                    const phaseTree = phaseTreeMap[x.parentId];
                    if (phaseTree) {
                        console.log("Linking group ", x, " to phase ", { ...phaseTree });
                        phaseTree.childItems = phaseTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        groupTreeMap[x.id] = checkpointItem;
                        phaseTree.childItems.push(checkpointItem);
                        this._sort(phaseTree.childItems);
                    }

                    const jobTree = jobTreeMap[x.parentId];
                    if (jobTree) {
                        console.log("Linking group ", x, " to job ", { ...jobTree });
                        jobTree.childItems = jobTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        groupTreeMap[x.id] = checkpointItem;
                        jobTree.childItems.push(checkpointItem);
                        this._sort(jobTree.childItems);
                    }

                    const taskTree = taskTreeMap[x.parentId];
                    if (taskTree) {
                        console.log("Linking group ", x, " to task ", { ...taskTree });
                        taskTree.childItems = taskTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        groupTreeMap[x.id] = checkpointItem;
                        taskTree.childItems.push(checkpointItem);
                        this._sort(taskTree.childItems);
                    }
                });

                console.log("Groups:", groups, { ...items }, groupTreeMap);

                // go one more round, recursive level1, checkpoint can be child of another checkpoint
                checkpoints.forEach(x => {
                    const checkpointTree = checkpointTreeMap[x.parentId];
                    if (checkpointTree) {
                        console.log("Linking checkpoint ", x, " to checkpoint ", { ...checkpointTree });
                        checkpointTree.childItems = checkpointTree.childItems || [];
                        const checkpointItem = {
                            data: this._getItem(x)
                        };
                        checkpointTreeMap[x.id] = checkpointItem;
                        checkpointTree.childItems.push(checkpointItem);
                        this._sort(checkpointTree.childItems);
                    }
                });

                const content = `Timeline Id: ${t.id},  URL: ${t.url}`;
                console.log("Tree items:", items);
                const provider = new TreeItemProvider(items);
                if (timelineId) {
                    this.setState({
                        sContent: content,
                        sItemProvider: provider
                    });
                }
                else {
                    this.setState({
                        content: content,
                        itemProvider: provider
                    });
                }
            });
        }
    }

    private _getItem(t: TimelineRecord, dangling?: boolean) {
        const item: ITimelineItem = { ...t } as ITimelineItem;
        item.computedName = `${t.type}   :   ${t.id}   :  order ${t.order}   :   ${t.name}`;
        if (dangling) {
            item.computedName = `Dangling => ${item.computedName}`;
        }
        return item;
    }

    private _sort(items: ITimelineTreeItem[]) {
        items = items.sort(this._sortRecords);
    }

    private _sortRecords = (a: ITimelineTreeItem, b: ITimelineTreeItem) => {
        if (a.data.order > b.data.order) {
            return 1;
        }
        if (a.data.order < b.data.order) {
            return -1;
        }
        return 0;
    }
}
// tslint:enable:member-ordering

